import type { RuleContextSchemaDto, ContextFieldDto } from '@/types/core/Rules';

/**
 * Builds the `ConditionEditor` context schema for a PlanView row-axis composable filter
 * (PlanView-RowAxis-Composition Phase 2) from the filterable fields the **owning module** exposes.
 *
 * The fields are NOT hardcoded here: they are fetched per dimension from
 * `GET /api/finance/planline/row-axis-filter-fields/{dimensionDefinitionId}`, which Finance answers by
 * asking the backing entity's owning module (Inventory ProductGroup / Sales Customer). That module is
 * the single source of truth — the same declaration also backs the server-side resolver, so the editor
 * only ever offers fields the resolver can evaluate. This module just maps that metadata into the shape
 * the reused editor consumes.
 */

/** Mirror of the backend `RowAxisFilterFieldDto` (Nexo.Core.Conditions). */
export interface RowAxisFilterFieldDto {
  field: string;
  /** English fallback, straight from the DSL-generated entity metadata. */
  label: string;
  /**
   * `DisplayName:{Entity}.{field}`, when the owning module declares one. Resolved here rather than on
   * the server because the key may belong to another module's resource and a module's localizer only
   * sees its own — the client has them all merged into one dictionary.
   */
  labelKey?: string;
  /** What the field belongs to ("Konto", or a dimension's own name); already display-ready. */
  groupLabel?: string;
  type: string;
  operators: string[];
  enumSource?: string;
  /**
   * Options served with the field, for a domain that is data rather than a C# enum — a financial
   * dimension's values, which the client's enum registry has no type to resolve.
   */
  enumValues?: { value: string; label: string }[];
}

/**
 * The label to show for one filterable field: its `DisplayName:` translation where the owning module
 * declares one, the DSL's English label otherwise, prefixed with what it belongs to.
 *
 * Composed here rather than server-side, and that is the whole point. The entity metadata's label is
 * the DSL's, which is authored in English, so a server-composed label put an untranslated field name
 * behind a translated prefix -- "Konto · Account Number" on an otherwise Swedish screen. The prefix is
 * the only part the server can finish: it is either a localized string of its own or a dimension's
 * name, which is tenant data and translates to nothing.
 *
 * @param translate an i18next `t`; called with the metadata label as the fallback, so a field whose
 *   module declares no `DisplayName:` key reads exactly as it did before.
 */
export function resolveFilterFieldLabel(
  translate: (key: string, options: { defaultValue: string }) => string,
  field: Pick<RowAxisFilterFieldDto, 'label' | 'labelKey' | 'groupLabel'>,
): string {
  const name = field.labelKey
    ? translate(field.labelKey, { defaultValue: field.label })
    : field.label;
  return field.groupLabel ? `${field.groupLabel} · ${name}` : name;
}

/** Map the owning module's filterable fields into the editor's RuleContextSchema. */
export function buildRowAxisContextSchema(
  fields: RowAxisFilterFieldDto[] | undefined,
  entity: string,
): RuleContextSchemaDto {
  return {
    entity,
    version: 1,
    objects: [
      {
        name: entity,
        fields: (fields ?? []).map<ContextFieldDto>((f) => ({
          path: f.field,
          label: f.label,
          type: f.type,
          operators: f.operators,
          ...(f.enumValues?.length
            ? { enum: { values: f.enumValues } }
            : f.enumSource ? { enum: { source: f.enumSource } } : {}),
        })),
      },
    ],
  };
}
